import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { extractPDFPages, processSinglePage, createPDFFromImages } from '../utils/pdfProcessor';
import { downloadPDF } from '../utils/pdfProcessor';
import { savePDFSettings, loadPDFSettings } from '../utils/settingsPersistence';

export function useLazyPDFProcessor(pdfFile, currentPageIndex) {
  const [filters, setFilters] = useState({
    showOriginal: true,
    invert: false, // Changed from true - no invert by default
    grayscale: false,
    brightness: 100,
    contrast: 100,
    removeBackground: false,
    bgRemovalTolerance: 0.15,
    bgSoftenEdges: false,
  });

  const [pageExceptions, setPageExceptions] = useState({});
  const [savedPresets, setSavedPresets] = useState([]);

  const [extractedPages, setExtractedPages] = useState([]); // Raw pages from PDF
  const [processedPagesCache, setProcessedPagesCache] = useState({}); // Cache: pageIndex -> processed page
  const [isExtracting, setIsExtracting] = useState(false);
  const [isProcessingPage, setIsProcessingPage] = useState(false);
  const [extractProgress, setExtractProgress] = useState({ current: 0, total: 0, phase: '' });
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);

  // Helper function to check if global filters have any adjustments
  const hasGlobalAdjustments = useCallback(() => {
    const defaultFilters = {
      invert: false,
      grayscale: false,
      brightness: 100,
      contrast: 100,
      removeBackground: false,
      bgRemovalTolerance: 0.15,
      bgSoftenEdges: false,
    };

    return (
      filters.invert !== defaultFilters.invert ||
      filters.grayscale !== defaultFilters.grayscale ||
      filters.brightness !== defaultFilters.brightness ||
      filters.contrast !== defaultFilters.contrast ||
      filters.removeBackground !== defaultFilters.removeBackground
    );
  }, [filters]);

  // Helper function to get effective filters for a page
  const getEffectiveFilters = useCallback((pageNumber) => {
    const exception = pageExceptions[pageNumber];

    // Check if page has exception with useGlobal flag
    if (exception && exception.enabled) {
      // If page marked to use global filters, return global filters
      if (exception.useGlobal) {
        return filters;
      }
      // Otherwise use page's custom settings
      return exception.settings;
    }

    // Pages default to identity filters (no adjustments)
    // Global filters are NOT applied unless page explicitly marked with useGlobal
    return {
      invert: false,
      grayscale: false,
      brightness: 100,
      contrast: 100,
      removeBackground: false,
      bgRemovalTolerance: 0.15,
      bgSoftenEdges: false,
    };
  }, [filters, pageExceptions, hasGlobalAdjustments]);

  // Settings for batch processing
  const [settings, setSettings] = useState({
    batchSize: 5, // Process pages in groups of X
    updateMode: 'lazy', // 'lazy', 'batch', or 'all'
  });

  const filtersRef = useRef(filters);

  // Keep filters ref up to date
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Auto-save settings when filters or pageExceptions change
  useEffect(() => {
    if (!pdfFile) return;

    const filename = pdfFile.name;
    // Don't save showOriginal to localStorage - it should always default to true
    const { showOriginal, ...filtersToSave } = filters;
    savePDFSettings(filename, {
      filters: filtersToSave,
      pageExceptions,
    });
  }, [filters, pageExceptions, pdfFile]);

  // Load saved settings when PDF file changes
  useEffect(() => {
    if (!pdfFile) return;

    const filename = pdfFile.name;
    const savedSettings = loadPDFSettings(filename);

    if (savedSettings) {
      console.log('Restoring saved settings for:', filename);
      if (savedSettings.filters) {
        // Restore saved filters but always keep showOriginal: true (not saved to localStorage)
        setFilters(prev => ({
          ...prev, // Keep default values including showOriginal: true
          ...savedSettings.filters, // Restore other saved filters
          showOriginal: true, // Force showOriginal to always be true (never restore from localStorage)
        }));
      }
      if (savedSettings.pageExceptions) {
        setPageExceptions(savedSettings.pageExceptions);
      }
      // Clear cache when loading new settings
      setProcessedPagesCache({});
    }
  }, [pdfFile]);

  // Step 1: Extract all pages from PDF (only once when file changes)
  useEffect(() => {
    if (!pdfFile) {
      setExtractedPages([]);
      setProcessedPagesCache({});
      setIsExtracting(false);
      setExtractProgress({ current: 0, total: 0, phase: '' });
      setError(null);
      return;
    }

    const extractPages = async () => {
      setIsExtracting(true);
      setError(null);
      console.log('Extracting PDF pages...');

      try {
        const pages = await extractPDFPages(pdfFile, (progressInfo) => {
          setExtractProgress(progressInfo);
        });

        setExtractedPages(pages);
        console.log(`Extracted ${pages.length} pages from PDF`);
      } catch (err) {
        console.error('Failed to extract PDF pages:', err);
        setError(err.message);
      } finally {
        setIsExtracting(false);
      }
    };

    extractPages();
  }, [pdfFile]);

  // Step 2: Process current page on demand when page changes or filters change
  useEffect(() => {
    if (extractedPages.length === 0 || currentPageIndex < 0 || currentPageIndex >= extractedPages.length) {
      return;
    }

    const processCurrentPage = async () => {
      // Get effective filters for this page (exception or global)
      const pageNumber = currentPageIndex + 1;
      const exception = pageExceptions[pageNumber];

      // If page is marked as "preserve original", skip processing entirely
      if (exception?.preserveOriginal) {
        console.log(`Page ${pageNumber} is preserved - skipping processing`);
        return;
      }

      const effectiveFilters = getEffectiveFilters(pageNumber);

      // Check if this page with these exact filter settings is already cached
      const cacheKey = `${currentPageIndex}-${JSON.stringify(effectiveFilters)}`;

      if (processedPagesCache[cacheKey]) {
        console.log(`Page ${pageNumber} already processed with current filters`);
        return;
      }

      setIsProcessingPage(true);
      console.log(`Processing page ${pageNumber} on demand...`);

      try {
        const page = extractedPages[currentPageIndex];
        const processedPage = await processSinglePage(page, effectiveFilters);

        setProcessedPagesCache(prev => ({
          ...prev,
          [cacheKey]: processedPage
        }));

        console.log(`Page ${pageNumber} processed successfully`);
      } catch (err) {
        console.error(`Failed to process page ${pageNumber}:`, err);
        setError(err.message);
      } finally {
        setIsProcessingPage(false);
      }
    };

    processCurrentPage();
  }, [currentPageIndex, filters, extractedPages, processedPagesCache, getEffectiveFilters, pageExceptions]);

  // Get the current processed page
  const getCurrentProcessedPage = useCallback(() => {
    const pageNumber = currentPageIndex + 1;
    const exception = pageExceptions[pageNumber];

    // If page is marked as "preserve original", return the original image as "processed"
    if (exception?.preserveOriginal) {
      const originalPage = extractedPages[currentPageIndex];
      if (originalPage) {
        // Return the original page with processedDataUrl pointing to original image
        return {
          ...originalPage,
          processedDataUrl: originalPage.imageDataUrl
        };
      }
      return null;
    }

    const effectiveFilters = getEffectiveFilters(pageNumber);
    const cacheKey = `${currentPageIndex}-${JSON.stringify(effectiveFilters)}`;
    return processedPagesCache[cacheKey] || null;
  }, [currentPageIndex, getEffectiveFilters, processedPagesCache, pageExceptions, extractedPages]);

  const updateFilter = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));

    // Clear cache when filters change (will reprocess on demand)
    if (settings.updateMode === 'lazy') {
      setProcessedPagesCache({});
    }
  };

  const resetFilters = () => {
    setFilters({
      showOriginal: true,
      invert: false, // Changed from true - no invert by default
      grayscale: false,
      brightness: 100,
      contrast: 100,
      removeBackground: false,
      bgRemovalTolerance: 0.15,
      bgSoftenEdges: false,
    });
    setProcessedPagesCache({}); // Clear cache
  };

  // Batch process pages
  const batchProcessPages = useCallback(async (pageIndices) => {
    if (extractedPages.length === 0) return;

    console.log(`Batch processing ${pageIndices.length} pages...`);
    const currentFilters = filtersRef.current;

    for (const pageIndex of pageIndices) {
      if (pageIndex < 0 || pageIndex >= extractedPages.length) continue;

      const cacheKey = `${pageIndex}-${JSON.stringify(currentFilters)}`;

      // Skip if already cached
      if (processedPagesCache[cacheKey]) continue;

      try {
        const page = extractedPages[pageIndex];
        const processedPage = await processSinglePage(page, currentFilters);

        setProcessedPagesCache(prev => ({
          ...prev,
          [cacheKey]: processedPage
        }));
      } catch (err) {
        console.error(`Failed to batch process page ${pageIndex + 1}:`, err);
      }
    }
  }, [extractedPages, processedPagesCache]);

  // Process multiple pages with different filter settings (for contact sheet)
  const processMultiplePages = useCallback(async (pagesToProcess) => {
    // pagesToProcess is array of { pageIndex, filters }
    if (!pagesToProcess || pagesToProcess.length === 0) return;

    console.log(`Processing ${pagesToProcess.length} pages with custom filters...`);

    const promises = pagesToProcess.map(async ({ pageIndex, filters }) => {
      const page = extractedPages[pageIndex];
      if (!page) return null;

      const cacheKey = `${pageIndex}-${JSON.stringify(filters)}`;

      // Skip if already cached
      if (processedPagesCache[cacheKey]) {
        console.log(`Page ${pageIndex + 1} already cached`);
        return null;
      }

      try {
        const processedPage = await processSinglePage(page, filters);

        console.log(`[Cache] Adding page ${pageIndex + 1} with cacheKey:`, cacheKey.substring(0, 100));

        setProcessedPagesCache(prev => ({
          ...prev,
          [cacheKey]: processedPage
        }));

        console.log(`Page ${pageIndex + 1} processed successfully`);
        return processedPage;
      } catch (error) {
        console.error(`Error processing page ${pageIndex + 1}:`, error);
        return null;
      }
    });

    await Promise.all(promises);
    console.log('Batch processing complete');
  }, [extractedPages, processedPagesCache]);

  // Process all pages
  const processAllPages = useCallback(async () => {
    if (extractedPages.length === 0) return;

    console.log('Processing all pages...');
    setIsProcessingPage(true);
    setProcessingProgress({ current: 0, total: extractedPages.length }); // Initialize progress
    const currentFilters = filtersRef.current;

    try {
      for (let i = 0; i < extractedPages.length; i++) {
        const pageIndex = i;
        const cacheKey = `${pageIndex}-${JSON.stringify(currentFilters)}`;

        if (processedPagesCache[cacheKey]) {
          setProcessingProgress({ current: i + 1, total: extractedPages.length });
          continue;
        }

        const page = extractedPages[pageIndex];
        const processedPage = await processSinglePage(page, currentFilters);

        setProcessedPagesCache(prev => ({
          ...prev,
          [cacheKey]: processedPage
        }));
        setProcessingProgress({ current: i + 1, total: extractedPages.length });
      }
      console.log('All pages processed successfully');
    } catch (err) {
      console.error('Failed to process all pages:', err);
      setError(err.message);
    } finally {
      setIsProcessingPage(false);
      setProcessingProgress({ current: 0, total: 0 }); // Reset progress
    }
  }, [extractedPages, processedPagesCache]);

  // Download processed PDF
  const downloadProcessedPDF = useCallback(async () => {
    if (extractedPages.length === 0) {
      console.error('No pages to download');
      return;
    }

    console.log('Preparing PDF for download...');
    const currentFilters = filtersRef.current;

    try {
      // Process all pages if not already processed
      const processedPages = [];
      for (let i = 0; i < extractedPages.length; i++) {
        const pageNumber = i + 1;
        const exception = pageExceptions[pageNumber];

        // If page is marked as "preserve original", use original image
        if (exception?.preserveOriginal) {
          console.log(`Page ${pageNumber} is preserved - using original image`);
          processedPages.push({
            ...extractedPages[i],
            processedDataUrl: extractedPages[i].imageDataUrl
          });
          continue;
        }

        // Get effective filters for this page (exception or global)
        const effectiveFilters = exception?.enabled ? exception.settings : currentFilters;
        const cacheKey = `${i}-${JSON.stringify(effectiveFilters)}`;

        if (processedPagesCache[cacheKey]) {
          processedPages.push(processedPagesCache[cacheKey]);
        } else {
          // Process on the fly if not cached
          const processedPage = await processSinglePage(extractedPages[i], effectiveFilters);
          processedPages.push(processedPage);
        }
      }

      // Create PDF
      const pdfBytes = await createPDFFromImages(processedPages, () => {});

      const originalName = pdfFile.name || 'document.pdf';
      const filename = originalName.replace('.pdf', '-optimized.pdf');
      downloadPDF(pdfBytes, filename);
    } catch (err) {
      console.error('Download failed:', err);
      alert(`Download failed with error: ${err.message}`);
    }
  }, [pdfFile, extractedPages, processedPagesCache, pageExceptions]);

  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  // Page exception management functions
  const addPageException = useCallback((pageNumber, settings, label = '', autoOptimized = false) => {
    console.log(`[addPageException] Adding exception for page ${pageNumber}`);
    console.log(`[addPageException] settings:`, settings);
    console.log(`[addPageException] label:`, label);

    setPageExceptions(prev => {
      const newExceptions = {
        ...prev,
        [pageNumber]: {
          settings,
          enabled: true,
          label,
          autoOptimized
        }
      };
      console.log(`[addPageException] New pageExceptions:`, newExceptions);
      return newExceptions;
    });

    // Invalidate cache for this page
    setProcessedPagesCache(prev => {
      const newCache = { ...prev };
      Object.keys(newCache).forEach(key => {
        if (key.startsWith(`${pageNumber - 1}-`)) {
          console.log(`[addPageException] Invalidating cache key: ${key}`);
          delete newCache[key];
        }
      });
      return newCache;
    });
  }, []);

  const updatePageException = useCallback((pageNumber, settings, label) => {
    setPageExceptions(prev => ({
      ...prev,
      [pageNumber]: {
        ...prev[pageNumber],
        settings,
        label
      }
    }));
    // Invalidate cache for this page
    setProcessedPagesCache(prev => {
      const newCache = { ...prev };
      Object.keys(newCache).forEach(key => {
        if (key.startsWith(`${pageNumber - 1}-`)) {
          delete newCache[key];
        }
      });
      return newCache;
    });
  }, []);

  const deletePageException = useCallback((pageNumber) => {
    setPageExceptions(prev => {
      const newExceptions = { ...prev };
      delete newExceptions[pageNumber];
      return newExceptions;
    });
    // Invalidate cache for this page
    setProcessedPagesCache(prev => {
      const newCache = { ...prev };
      Object.keys(newCache).forEach(key => {
        if (key.startsWith(`${pageNumber - 1}-`)) {
          delete newCache[key];
        }
      });
      return newCache;
    });
  }, []);

  const togglePageException = useCallback((pageNumber) => {
    setPageExceptions(prev => ({
      ...prev,
      [pageNumber]: {
        ...prev[pageNumber],
        enabled: !prev[pageNumber].enabled
      }
    }));
    // Invalidate cache for this page
    setProcessedPagesCache(prev => {
      const newCache = { ...prev };
      Object.keys(newCache).forEach(key => {
        if (key.startsWith(`${pageNumber - 1}-`)) {
          delete newCache[key];
        }
      });
      return newCache;
    });
  }, []);

  // Batch operations for multiple pages
  const applyToSelectedPages = useCallback((pageNumbers, label = '') => {
    const currentFilters = filtersRef.current;
    setPageExceptions(prev => {
      const newExceptions = { ...prev };
      pageNumbers.forEach(pageNumber => {
        newExceptions[pageNumber] = {
          settings: currentFilters,
          enabled: true,
          label
        };
      });
      return newExceptions;
    });
    // Invalidate cache for these pages
    setProcessedPagesCache(prev => {
      const newCache = { ...prev };
      pageNumbers.forEach(pageNumber => {
        Object.keys(newCache).forEach(key => {
          if (key.startsWith(`${pageNumber - 1}-`)) {
            delete newCache[key];
          }
        });
      });
      return newCache;
    });
  }, []);

  const preservePagesAsOriginal = useCallback((pageNumbers, shouldPreserve = true) => {
    setPageExceptions(prev => {
      const newExceptions = { ...prev };
      pageNumbers.forEach(pageNumber => {
        if (newExceptions[pageNumber]) {
          // When preserving, clear any custom settings and override with preserve flag
          newExceptions[pageNumber] = {
            settings: filtersRef.current,  // Reset to global filters (no custom settings)
            label: '',  // Clear any preset label
            useGlobal: false,  // Clear global flag
            preserveOriginal: shouldPreserve,
            enabled: shouldPreserve  // Enable exception when preserving
          };
        } else {
          newExceptions[pageNumber] = {
            settings: filtersRef.current,
            enabled: shouldPreserve,  // Enable exception when preserving
            preserveOriginal: shouldPreserve
          };
        }
      });
      return newExceptions;
    });
    // Invalidate cache for these pages
    setProcessedPagesCache(prev => {
      const newCache = { ...prev };
      pageNumbers.forEach(pageNumber => {
        Object.keys(newCache).forEach(key => {
          if (key.startsWith(`${pageNumber - 1}-`)) {
            delete newCache[key];
          }
        });
      });
      return newCache;
    });
  }, []);

  // Apply global settings to specific pages (creates exceptions with useGlobal flag)
  const applyGlobalToPages = useCallback((pageNumbers) => {
    setPageExceptions(prev => {
      const newExceptions = { ...prev };
      pageNumbers.forEach(pageNumber => {
        newExceptions[pageNumber] = {
          settings: filtersRef.current,  // Use current global filters
          enabled: true,
          useGlobal: true,  // Flag to indicate this page should use global settings
          label: 'Global Settings'
        };
      });
      return newExceptions;
    });
    // Invalidate cache for these pages
    setProcessedPagesCache(prev => {
      const newCache = { ...prev };
      pageNumbers.forEach(pageNumber => {
        Object.keys(newCache).forEach(key => {
          if (key.startsWith(`${pageNumber - 1}-`)) {
            delete newCache[key];
          }
        });
      });
      return newCache;
    });
  }, []);

  // Preset management functions
  const savePreset = useCallback((name, filterSettings) => {
    const newPreset = {
      id: Date.now().toString(),
      name,
      settings: filterSettings,
      createdAt: new Date().toISOString()
    };

    setSavedPresets(prev => [...prev, newPreset]);

    // Save to localStorage
    try {
      const updatedPresets = [...savedPresets, newPreset];
      localStorage.setItem('printOptimizer_presets', JSON.stringify(updatedPresets));
      console.log(`Preset saved: ${name}`);
    } catch (error) {
      console.error('Failed to save preset:', error);
    }

    return newPreset;
  }, [savedPresets]);

  const deletePreset = useCallback((presetId) => {
    setSavedPresets(prev => prev.filter(p => p.id !== presetId));

    // Update localStorage
    try {
      const updatedPresets = savedPresets.filter(p => p.id !== presetId);
      localStorage.setItem('printOptimizer_presets', JSON.stringify(updatedPresets));
      console.log(`Preset deleted: ${presetId}`);
    } catch (error) {
      console.error('Failed to delete preset:', error);
    }
  }, [savedPresets]);

  const applyPresetToPages = useCallback((presetId, pageNumbers) => {
    const preset = savedPresets.find(p => p.id === presetId);
    if (!preset) {
      console.error('Preset not found:', presetId);
      return;
    }

    setPageExceptions(prev => {
      const newExceptions = { ...prev };
      pageNumbers.forEach(pageNumber => {
        newExceptions[pageNumber] = {
          settings: preset.settings,
          enabled: true,
          label: preset.name,
          presetId: presetId
        };
      });
      return newExceptions;
    });

    // Invalidate cache for these pages
    setProcessedPagesCache(prev => {
      const newCache = { ...prev };
      pageNumbers.forEach(pageNumber => {
        Object.keys(newCache).forEach(key => {
          if (key.startsWith(`${pageNumber - 1}-`)) {
            delete newCache[key];
          }
        });
      });
      return newCache;
    });

    console.log(`Applied preset "${preset.name}" to ${pageNumbers.length} pages`);
  }, [savedPresets]);

  // Load presets from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('printOptimizer_presets');
      if (stored) {
        const presets = JSON.parse(stored);
        setSavedPresets(presets);
        console.log(`Loaded ${presets.length} saved presets`);
      }
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  }, []);

  // Memoize currentProcessedPage so it properly triggers re-renders
  const currentProcessedPage = useMemo(() => {
    return getCurrentProcessedPage();
  }, [getCurrentProcessedPage]);

  console.log('useLazyPDFProcessor returning:', {
    extractedPagesLength: extractedPages.length,
    currentProcessedPage,
    hasCurrentProcessedPage: !!currentProcessedPage
  });

  return {
    extractedPages,
    currentProcessedPage,
    filters,
    updateFilter,
    resetFilters,
    downloadProcessedPDF,
    isExtracting,
    isProcessingPage,
    extractProgress,
    error,
    settings,
    updateSettings,
    processingProgress,
    processAllPages,
    processMultiplePages,
    // Page exceptions
    pageExceptions,
    addPageException,
    updatePageException,
    deletePageException,
    togglePageException,
    getEffectiveFilters,
    batchProcessPages,
    // Batch operations
    applyToSelectedPages,
    preservePagesAsOriginal,
    applyGlobalToPages,
    // Cache for contact sheet
    processedPagesCache,
    // Preset management
    savedPresets,
    savePreset,
    deletePreset,
    applyPresetToPages,
  };
}
